import { formatNumber } from '../api';
import type { RuleCheck, RuleTrace, ScoreBreakdown, TraceLevel } from '../types';
import { Badge } from './ui';

/**
 * A decision's rule trace, laid out as the rule ran: the checks it made (value
 * against what it needed), the score as the sum of its signals, and the levels it
 * set. The trace is decoded from persisted JSON, so every field is guarded.
 */

const signed = (value: number, digits = 2): string => `${value >= 0 ? '+' : '−'}${Math.abs(value).toFixed(digits)}`;

const OUTCOME_LABEL: Record<string, string> = {
  open_long: 'entry',
  open_short: 'short entry',
  close_long: 'exit',
  close_short: 'cover',
  stop_long: 'stop',
  stop_short: 'stop',
};

const shortDate = (date: string): string =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

/** True when a decoded value looks like a trace this view can render. */
export function isRuleTrace(value: unknown): value is RuleTrace {
  return typeof value === 'object' && value !== null && Array.isArray((value as RuleTrace).checks);
}

export function RuleChecks({ checks, compact = false }: { checks: RuleCheck[]; compact?: boolean }) {
  return (
    <div className="grid-wrap">
      <table className="trace-checks" aria-label="Rule checks">
        <thead>
          <tr>
            <th scope="col">Rule</th>
            <th scope="col">Observed</th>
            <th scope="col">Needed</th>
            <th scope="col">Result</th>
          </tr>
        </thead>
        <tbody>
          {checks.map((check, index) => (
            <tr key={`${check.rule}-${index}`} className={check.decisive ? 'trace-decisive' : undefined}>
              <td>
                {check.rule}
                {!compact && check.note && <div className="mini">{check.note}</div>}
              </td>
              <td className="mono-small trace-value">{check.value}</td>
              <td className="mono-small trace-value">{check.test}</td>
              <td className={`trace-result ${check.passed ? 'trace-pass' : 'trace-fail'}`}>
                {check.passed ? '✓ met' : '✗ not met'}
                {check.decisive && <span className="trace-decided"> decided it</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ScoreTable({ score, threshold }: { score: ScoreBreakdown; threshold?: { label: string; value: number } }) {
  const counted = score.signals.filter((signal) => signal.counted);
  const shown = score.signals.filter((signal) => !signal.counted);
  return (
    <div>
      <p className="reason" style={{ margin: '0 0 8px' }}>
        score = tanh((bullish {score.bullish.toFixed(2)} − bearish {score.bearish.toFixed(2)}) / {score.scale}) ={' '}
        <b style={{ color: 'var(--ink)' }}>{signed(score.score)}</b>
        {threshold && (
          <>
            {' '}
            against the {threshold.label} threshold of {signed(threshold.value)}
          </>
        )}
      </p>
      <div className="grid-wrap">
        <table aria-label="Signals in the score">
          <thead>
            <tr>
              <th scope="col">Signal</th>
              <th scope="col">Read from</th>
              <th scope="col" className="num">
                Strength × weight
              </th>
              <th scope="col" className="num">
                Effect
              </th>
            </tr>
          </thead>
          <tbody>
            {counted.map((signal) => (
              <tr key={signal.name}>
                <td>
                  <Badge tone={signal.direction === 'bullish' ? 'green' : signal.direction === 'bearish' ? 'red' : 'grey'}>
                    {signal.name}
                  </Badge>
                  <div className="mini">{signal.detail}</div>
                </td>
                <td className="mono-small" style={{ whiteSpace: 'nowrap' }}>
                  {signal.from && signal.to && signal.from !== signal.to
                    ? `${shortDate(signal.from)} → ${shortDate(signal.to)}`
                    : signal.to
                      ? shortDate(signal.to)
                      : '—'}
                </td>
                <td className="num">
                  {signal.strength.toFixed(2)} × {signal.weight.toFixed(1)}
                  <div className="mini">{signal.category}</div>
                </td>
                <td className={`num ${signal.contribution >= 0 ? 'up' : 'down'}`}>{signed(signal.contribution)}</td>
              </tr>
            ))}
            {shown.map((signal) => (
              <tr key={signal.name} className="trace-excluded">
                <td>
                  {signal.name}
                  <div className="mini">{signal.excluded}</div>
                </td>
                <td className="mono-small" style={{ whiteSpace: 'nowrap' }}>
                  {signal.to ? shortDate(signal.to) : '—'}
                </td>
                <td className="num">{signal.strength.toFixed(2)}</td>
                <td className="num">0</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Levels({ levels, currency }: { levels: TraceLevel[]; currency: string }) {
  if (levels.length === 0) return null;
  return (
    <div className="trace-levels">
      {levels.map((level) => (
        <span key={`${level.label}${level.price}`} className={`trace-level trace-level-${level.kind}`}>
          {level.label} <b>{formatNumber(level.price, 2)}</b>
        </span>
      ))}
      <span className="mono-small">{currency}</span>
    </div>
  );
}

export default function RuleTraceView({
  trace,
  thresholdLabel,
}: {
  trace: RuleTrace;
  /** Shown beside the score, e.g. { label: 'buy', value: 0.3 }. */
  thresholdLabel?: { label: string; value: number };
}) {
  const checks = Array.isArray(trace.checks) ? trace.checks : [];
  const levels = Array.isArray(trace.levels) ? trace.levels.filter((level) => typeof level.price === 'number') : [];
  const currency = trace.currency ?? '';
  const sizing = trace.sizing;
  const position = trace.position;
  const momentum = trace.momentum;

  return (
    <div className="trace">
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        {trace.outcome && <Badge tone="amber">{OUTCOME_LABEL[trace.outcome] ?? trace.outcome} rule</Badge>}
        <h3 className="trace-headline">{trace.headline ?? 'Rule trace'}</h3>
      </div>
      {trace.bar?.date && (
        <p className="mono-small" style={{ margin: '6px 0 0' }}>
          read the {shortDate(trace.bar.date)} bar
          {trace.bar.provisional ? ' while that session was trading (its close was the live price)' : ''}
          {typeof trace.price === 'number' ? ` · price ${formatNumber(trace.price, 2)} ${currency}` : ''}
          {trace.regime?.regime ? ` · market regime ${trace.regime.regime.toUpperCase()}` : ''}
        </p>
      )}

      {checks.length > 0 && (
        <section className="trace-section">
          <h4>Rule checks</h4>
          <RuleChecks checks={checks} />
        </section>
      )}

      {trace.score && Array.isArray(trace.score.signals) && (
        <section className="trace-section">
          <h4>Pattern score</h4>
          <ScoreTable score={trace.score} {...(thresholdLabel ? { threshold: thresholdLabel } : {})} />
        </section>
      )}

      {momentum && typeof momentum.rank === 'number' && (
        <section className="trace-section">
          <h4>Momentum rank</h4>
          <p className="reason" style={{ margin: 0 }}>
            Rank {momentum.rank}
            {typeof momentum.of === 'number' ? ` of ${momentum.of}` : ''}
            {typeof momentum.momentumPct === 'number' ? `, 12-1 momentum ${signed(momentum.momentumPct, 1)}%` : ''}
            {typeof momentum.pctFrom52WeekHigh === 'number'
              ? `, ${Math.abs(momentum.pctFrom52WeekHigh).toFixed(1)}% below the 52-week high`
              : ''}
            {typeof momentum.composite === 'number' ? ` (composite ${momentum.composite.toFixed(2)})` : ''}.
          </p>
        </section>
      )}

      {(levels.length > 0 || sizing || position) && (
        <section className="trace-section">
          <h4>Levels and size</h4>
          <Levels levels={levels} currency={currency} />
          {position && (
            <p className="reason" style={{ margin: '6px 0 0' }}>
              {position.direction === 'SHORT' ? 'Short' : 'Long'}
              {typeof position.entryPrice === 'number' ? ` from ${formatNumber(position.entryPrice, 2)}` : ''}
              {typeof position.pnlPct === 'number' ? `, P&L ${signed(position.pnlPct)}%` : ''}
              {typeof position.heldDays === 'number' ? `, held ${Math.floor(position.heldDays)} day(s)` : ''}. Loss stops
              compare the P&L after fees and currency; their price levels here are the equivalent before either.
            </p>
          )}
          {sizing && typeof sizing.positionPct === 'number' && (
            <p className="reason" style={{ margin: '6px 0 0' }}>
              Sized at {sizing.positionPct.toFixed(2)}% of equity, risking {sizing.riskedPct?.toFixed(2) ?? '?'}% if the{' '}
              {sizing.stopDistancePct?.toFixed(2) ?? '?'}% stop is hit
              {sizing.boundBy && sizing.boundBy !== 'risk-target' ? ` (capped by the ${sizing.boundBy.replace('-', ' ')})` : ''}.
            </p>
          )}
        </section>
      )}
    </div>
  );
}
